import { db, functions } from '../firebase'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

import { apiSlice } from './apiSlice'

// Helper type for error responses
interface FirebaseError {
  code?: string
  message: string
}

export const clubsApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({

    // Create a new club
    createClub: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'createClub')
          const result = await callable(data)

          return { data: result.data }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
                errors: error.details || null
              },
            },
          }
        }
      },
      invalidatesTags: [{ type: 'Clubs' }]
    }),

    // Fetch list of clubs using Firebase SDK
    fetchClubs: builder.query({
      async queryFn() {
        try {
          const querySnapshot = await getDoc(doc(db, 'clubsSummary', 'clubsMap'))
          if (querySnapshot.exists()) {
            const clubsMap = querySnapshot.data()
            return { data: clubsMap }
          } else {
            return { data: {} }
          }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      providesTags: [{ type: 'Clubs' }]
    }),

    // Fetch club by clubId
    fetchClub: builder.query({
      async queryFn({ clubId, uid }) {
        try {
          const querySnapshot = await getDoc(doc(db, 'clubs', clubId))
          if (querySnapshot.exists()) {
            const clubData = querySnapshot.data()
            const id = querySnapshot.id

            // Determine if user is club secretary
            const isClubSecretary = clubData.staff.secretary.id === uid

            // Cteate staffById object so i can add each position to its member when mapping over the members to render the table
            const staffById: any = {}
            Object.entries(clubData.staff).forEach(([position, memberData]) => {
              const memberId = (memberData as any).id
              if (!staffById[memberId]) {
                staffById[memberId] = []
              }
              staffById[memberId].push(position)
            })

            return { data: { clubId: id, isClubSecretary, staffById, ...clubData } }
          } else {
            throw new Error('No club document found')
          }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      providesTags: (result, error, { clubId }) => [{ type: 'Club', id: clubId }]
    }),

    // Fetch club members
    fetchClubMembers: builder.query({
      async queryFn({ clubId }) {
        if (!clubId) {
          throw new Error("Club id is not provided")
        }

        try {
          const querySnapshot = await getDoc(doc(db, 'clubs', clubId, 'clubMembers', "members"))

          if (!querySnapshot.exists()) {
            throw new Error('No club document found')
          }

          const clubData = querySnapshot.data()

          return { data: clubData }
        } catch (error) {
          console.log(error)
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      providesTags: (result, error, { clubId }) => [{ type: 'ClubMembers', id: clubId }]
    }),

    // Send club invite to a user
    sendInvite: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'sendInvite')
          const result = await callable(data)

          return { data: result.data }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      }
    }),

    // Accept invite to club
    acceptInvite: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'acceptInvite')
          await callable(data)

          return { data: "Response sent successfully" }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      invalidatesTags: [{ type: 'User' }],
    }),

    // Decline invite to club
    declineInvite: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'declineInvite')
          await callable(data)

          return { data: "Response sent successfully" }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      async onQueryStarted({ inviteId, uid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          if (data) {
            dispatch(
              // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
              apiSlice.util.updateQueryData("fetchUserData", uid, (draft: any) => {
                const invite = draft?.invites?.[inviteId]
                if (invite) {
                  invite.status = "declined"
                  invite.read = true
                }
              })
            )
          }
        } catch (error) {
        }
      }
    }),

    // Accept user request to join club
    acceptJoinClubRequest: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'acceptJoinClubRequest')
          const result = await callable(data)

          return { data: (result.data as any).message }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      async onQueryStarted({ role, clubId, requestId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          if (role === "admin") {
            dispatch(
              // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
              apiSlice.util.updateQueryData("fetchAdminNotifications", undefined, (draft: any) => {
                const request = draft?.requests?.[requestId]
                if (request) request.status = "accepted"
              })
            )
            dispatch(
              // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
              apiSlice.util.updateQueryData("fetchClubs", undefined, (draft: any) => {
                if (draft?.[clubId]?.membersCount) {
                  draft[clubId].membersCount += 1
                }
              })
            )
          } else {
            dispatch(
              // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
              apiSlice.util.updateQueryData("fetchClubNotifications", clubId, (draft: any) => {
                const request = draft?.requests?.[requestId]
                if (request) request.status = "accepted"
              })
            )
          }
        } catch (error) {
          // Refetch fresh data if update fails
          dispatch(apiSlice.util.invalidateTags(["Notifications", "Club"]))
        }
      },
      invalidatesTags: (_, __, { clubId }) =>
        clubId ? [{ type: 'Club', id: clubId }, { type: 'ClubMembers', id: clubId }] : []
    }),

    // Decline user request to join club
    declineJoinClubRequest: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'declineJoinClubRequest')
          const result = await callable(data)

          return { data: (result.data as any).message }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      async onQueryStarted({ role, clubId, requestId }, { dispatch, queryFulfilled }) {
        // Optimistic update
        const isAdmin = role === "admin"
        const endpointRef = isAdmin ? "fetchAdminNotifications" : "fetchClubNotifications"
        const endpointArgs = isAdmin ? undefined : clubId

        dispatch(
          // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
          apiSlice.util.updateQueryData(endpointRef as any, endpointArgs, (draft: any) => {
            const request = draft?.requests?.[requestId]
            if (request) request.status = "declined"
          }),
        )

        try {
          await queryFulfilled
        } catch (error) {
          // Refetch fresh data if update fails
          dispatch(apiSlice.util.invalidateTags(["Notifications", "Club"]))
        }
      }
    }),

    // Fetch all notifications for specified club
    fetchClubNotifications: builder.query({
      async queryFn(clubId) {
        try {
          if (!clubId) {
            throw new Error('Club is not provided')
          }

          const docRef = doc(db, 'clubs', clubId, 'activity', 'notifications')
          const notificationsDoc = await getDoc(docRef)

          if (!notificationsDoc.exists()) {
            throw new Error('Could not retrieve data')
          }

          return { data: notificationsDoc.data() }
        } catch (error) {
          console.log(error)
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      providesTags: [{ type: 'Notifications', id: 'Club' }]
    }),

    // Clear club notifications
    clearClubNotifications: builder.mutation({
      async queryFn({ clubId, uid }, _queryApi) {
        // Update the cached data for club notifications in RTK Query
        _queryApi.dispatch(
          // @ts-expect-error - RTK Query cache update
          apiSlice.util.updateQueryData('fetchClub', { clubId, uid }, (draft: any) => {
            if (draft) {
              draft.notifications = 0
            }
          })
        )
        try {
          await updateDoc(doc(db, 'clubs', clubId), {
            notifications: 0
          })

          return { data: 'Notifications cleared' }
        } catch (error) {
          console.log(error)
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message
              }
            },
          }
        }
      },
    }),

    // Mark all club notifications as read
    clubNotificationsRead: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'clubNotificationsRead')
          await callable(data)

          return { data: 'Notifications marked as read' }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      async onQueryStarted({ requests, messages, clubId }, { dispatch, queryFulfilled }) {
        // Optimistic update
        dispatch(
          // @ts-expect-error - RTK Query cache update
          apiSlice.util.updateQueryData("fetchClubNotifications", clubId, (draft: any) => {
            if (!draft) return

            // Mark all specified request IDs as read
            requests.forEach((id) => {
              if (draft.requests?.[id]) {
                draft.requests[id].read = true
              }
            })

            // Mark all specified message IDs as read
            messages.forEach((id) => {
              if (draft.messages?.[id]) {
                draft.messages[id].read = true
              }
            })
          })
        )

        try {
          await queryFulfilled
        } catch (error) {
          // Refetch fresh data if update fails
          dispatch(apiSlice.util.invalidateTags(["Notifications"]))
        }
      }
    }),

    // Delete club notification
    deleteClubNotification: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'deleteClubNotification')
          await callable(data)

          return { data: 'Notification deleted' }
        } catch (error) {
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message
              }
            },
          }
        }
      },
      async onQueryStarted({ clubId, docId, category }, { dispatch, queryFulfilled }) {
        // Optimistic update
        dispatch(
          // @ts-expect-error - RTK Query cache update
          apiSlice.util.updateQueryData("fetchClubNotifications", clubId, (draft: any) => {
            if (draft?.[category]?.[docId]) {
              delete draft[category][docId]
            }
          }),
        )
        try {
          await queryFulfilled
        } catch (error) {
          // Refetch fresh data if update fails
          dispatch(apiSlice.util.invalidateTags(["Notifications", "Club"]))
        }
      }
    }),

    // Update club details
    updateClubDetails: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'updateClubDetails')
          const result = await callable(data)

          return { data: result.data }
        } catch (error) {
          console.log(error)
          const err = error as FirebaseError
          return {
            error: {
              status: err.code || 500,
              data: {
                message: err.message,
              },
            },
          }
        }
      },
      invalidatesTags: (result: any, _error, data: any): any => {
        const tags: any[] = [{ type: 'Club', id: data.clubId }]

        if (data.nameChanged === true) {
          tags.push({ type: 'Clubs' })

          if (result?.memberIds)
            result.memberIds.forEach((userId: string) => {
              tags.push({ type: 'User', id: userId })
            }
            )
        }

        return tags
      }
    }),

    // Update club address
    updateClubAddress: builder.mutation({
      async queryFn(data) {
        try {
          const callable = httpsCallable(functions, 'updateClubAddress')
          await callable(data)

          return { data: 'Club updated successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      invalidatesTags: (_result, _error, data) => [{ type: 'Club', id: data.clubId }]
    }),

    // Update member spaces
    updateMemberSpaces: builder.mutation({
      async queryFn({ spaces, clubId }) {
        try {
          if (!clubId || typeof spaces !== "number" || spaces < 0) {
            throw new Error('Correct data is not provided')
          }

          await updateDoc(doc(db, 'clubs', clubId), {
            membershipSpaces: spaces
          })

          return { data: 'Member spaces added successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      invalidatesTags: (_, __, { clubId }) => [{ type: 'Club', id: clubId }]
    }),

    // Delete user from club
    removeUserFromClub: builder.mutation({
      async queryFn(data) {
        try {
          if (!data.clubId || !data.userId) {
            throw new Error('Club or user is not provided')
          }

          const callable = httpsCallable(functions, 'removeUserFromClub')
          await callable(data)

          return { data: 'User deleted from club successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      async onQueryStarted({ clubId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          dispatch(
            // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
            apiSlice.util.updateQueryData("fetchClubs", undefined, (draft: any) => {
              if (draft?.[clubId]?.membersCount) {
                draft[clubId].membersCount -= 1
              }
            })
          )
        } catch (error) {
          dispatch(apiSlice.util.invalidateTags([{ type: "Club", id: clubId }]))
        }
      },
      invalidatesTags: (result, error, { clubId, userId }) => [
        { type: 'ClubMembers', id: clubId },
        { type: "User", id: userId }
      ]
    }),

    // Delete club
    deleteClub: builder.mutation({
      async queryFn(clubId) {
        try {
          if (!clubId) {
            throw new Error('Club id is not provided')
          }

          const callable = httpsCallable(functions, 'deleteClub')
          await callable(clubId)

          return { data: 'Club deleted successfully' }
        } catch (error) {
          return {
            error: {
              status: error.code || 500,
              data: {
                message: error.message,
              },
            },
          }
        }
      },
      async onQueryStarted(clubId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          // @ts-expect-error - RTK Query doesn't know about endpoints from other slices
          dispatch(apiSlice.util.updateQueryData("fetchClubs", undefined, (draft: any) => {
            if (draft?.[clubId]) {
              delete draft[clubId]
            }
          })
          )
        } catch (error) {
        }
      }
    })
  })
})

export const {
  useSendInviteMutation,
  useAcceptInviteMutation,
  useDeclineInviteMutation,
  useAcceptJoinClubRequestMutation,
  useDeclineJoinClubRequestMutation,
  useFetchClubNotificationsQuery,
  useClearClubNotificationsMutation,
  useClubNotificationsReadMutation,
  useFetchClubMembersQuery,
  useCreateClubMutation,
  useFetchClubsQuery,
  useFetchClubQuery,
  useUpdateClubDetailsMutation,
  useUpdateClubAddressMutation,
  useUpdateMemberSpacesMutation,
  useDeleteClubMutation,
  useDeleteClubNotificationMutation,
  useRemoveUserFromClubMutation
} = clubsApiSlice